import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

export const SUPPORTED_LANGUAGES = [
  { code: "ar", label: "العربية", dir: "rtl", flag: "🇸🇾" },
  { code: "en", label: "English", dir: "ltr", flag: "🇬🇧" },
];

const STORAGE_KEY = "tstore_lang";

const ar = require("./locales/ar").default;
const en = require("./locales/en").default;

const MESSAGES = { ar, en };

function getInitialLang() {
  if (typeof window === "undefined") return "ar";
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved && MESSAGES[saved]) return saved;
  const nav = (navigator.language || "ar").slice(0, 2);
  return MESSAGES[nav] ? nav : "ar";
}

function getDir(lang) {
  const found = SUPPORTED_LANGUAGES.find((l) => l.code === lang);
  return found ? found.dir : "rtl";
}

function interpolate(template, params) {
  if (!params) return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) =>
    params[key] !== undefined && params[key] !== null ? params[key] : ""
  );
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(getInitialLang);

  const dir = useMemo(() => getDir(lang), [lang]);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  }, [lang, dir]);

  const setLang = useCallback((next) => {
    if (!MESSAGES[next]) return;
    window.localStorage.setItem(STORAGE_KEY, next);
    setLangState(next);
  }, []);

  const t = useCallback(
    (key, params) => {
      const dict = MESSAGES[lang] || MESSAGES.ar;
      const value = key.split(".").reduce((acc, part) => {
        if (acc && typeof acc === "object" && part in acc) return acc[part];
        return undefined;
      }, dict);
      const final = typeof value === "string" ? value : key;
      return interpolate(final, params);
    },
    [lang]
  );

  const value = useMemo(
    () => ({ lang, dir, setLang, t, languages: SUPPORTED_LANGUAGES }),
    [lang, dir, setLang, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useTranslation must be used within an I18nProvider");
  }
  return ctx;
}

export default I18nContext;
